import { RcfUserSearchContext } from '@features/user/ui/RcfUserSearch/RcfUserSearchContext';
import {
  useActivateClubMemberMutation,
  useDeleteClubMemberMutation, useManagerClubMembersQuery,
  useRejectClubMemberMutation
} from '@features/club/api/hooks';
import { useRcfAttributesMutation, useRcfUsersQuery } from '@features/user/api/hooks';
import {
  ClubParticipantFormStatus,
  ClubParticipantsForm
} from '@features/club/ui/ClubParticipantsForm/ClubParticipantsForm';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useClubGroupProps } from '@features/club/hooks/useClubGroupProps';
import { RcfAttributes } from '@features/shared/api/typings';
import { useIsAdmin } from '@features/auth/hooks/useIsAdmin';

type Props = {
  clubId: string;
};

export function ClubParticipantsFormWidget({ clubId }: Props) {
  const { mutate: onDelete, isSuccess: isDeleteSuccess } = useDeleteClubMemberMutation(clubId);
  const { mutate: onAccept, isSuccess: isAcceptSuccess } = useActivateClubMemberMutation(clubId);
  const { mutate: onReject, isSuccess: isRejectSuccess } = useRejectClubMemberMutation(clubId);
  const { mutateAsync: updateRcfAttributes } = useRcfAttributesMutation();

  const [status, setStatus] = useState<ClubParticipantFormStatus>('Active');
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const currentUserId = user?.userId;

  const groupProps = useClubGroupProps(clubId);

  const [search, setSearch] = useState<string | null>(null);

  const {
    data: members,
    isPending,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useManagerClubMembersQuery(clubId, {
    sortBy: 'Alphabet',
    status,
    query: search || undefined,
  });

  const [query, setQuery] = useState('');
  const {
    data: rcfUsers,
    isFetching,
    hasNextPage: hasNextPageRcf,
    fetchNextPage: fetchNextPageRcf,
  } = useRcfUsersQuery({
    query,
    limit: 100,
  });

  const onChangeRcfAttributes = useCallback(
    async (value: RcfAttributes) => {
      const valueToSave: Partial<RcfAttributes> = { ...value };
      delete valueToSave.rcfIdApproved;

      await updateRcfAttributes({ items: [value] });
      refetch();
    },
    [updateRcfAttributes, refetch],
  );

  useEffect(() => {
    if (isDeleteSuccess || isAcceptSuccess || isRejectSuccess) {
      refetch();
    }
  }, [isDeleteSuccess, isAcceptSuccess, isRejectSuccess, refetch]);

  useEffect(() => {
    if (search !== null) {
      refetch();
    }
  }, [search, refetch]);

  return (
    <RcfUserSearchContext.Provider
      value={{
        query,
        setQuery,
        rcfUsers: query && rcfUsers ? rcfUsers : [],
        isLoading: isFetching,
        hasNextPage: hasNextPageRcf,
        fetchNextPage: fetchNextPageRcf,
      }}
    >
      <ClubParticipantsForm
        participants={members || []}
        onDelete={onDelete}
        onAccept={onAccept}
        onReject={onReject}
        isLoading={isPending}
        status={status}
        setStatus={setStatus}
        currentUserId={currentUserId}
        onLoadMore={hasNextPage ? fetchNextPage : undefined}
        onSearch={setSearch}
        isRcf={isAdmin}
        onRcfAttributesChange={onChangeRcfAttributes}
        {...groupProps}
      />
    </RcfUserSearchContext.Provider>
  );
}
